/* eslint-disable max-statements*/
import ImagePreviewView from './imagePreviewView';
import $ from 'jquery';
import 'Jcrop';
import Backbone from 'backbone';
import Handlebars from 'handlebars';
import React from 'react';
import {render as reactRender} from 'react-dom';
import eventChannels from '@sinclair-digital/stl-common/src/eventChannels';
import imageEditorTemplate from '../../templates/media/imageEditor.hbs';
import {messageHandler} from '@sinclair-digital/stl-common/src/sinclair/sinclair.uiTools';
import countErrorMessage from '@sinclair-digital/stl-common/src/util/countErrorMessage';
import {logError} from '@sinclair-digital/stl-common/src/analytics/common';
import {
    cancelImageChanges,
    changeImageAttribute,
    cropImage,
    previewImage,
    saveImageChanges,
} from '@sinclair-digital/stl-common/src/analytics/mediaPackage';
import {parse, stringify} from '@sinclair-digital/stl-common/src/sinclair/sinclair.markdown';
import {maxLength} from '@sinclair-digital/stl-common/src/util/validators';
import {cloneObject} from '@sinclair-digital/stl-common/src/sinclair/sinclair.utils';
import {getAvailableBrandUuids, getAvailableAdminBrandUuids} from '@sinclair-digital/stl-common/src/models/Brand';
import {DEFAULT_MEDIA_CAPTION_MAXLENGTH} from '@sinclair-digital/stl-common/src/models/media/mediaModel';
import {destroy} from '@sinclair-digital/stl-common/src/backboneHelpers';
import {MediaAccessRightsSidebarItem} from '@sinclair-digital/sbg-storyline-components/src/components/MediaAccessRights';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';

// sortOrderConfigure :: [String] -> ((String, String) -> Number)
export const sortOrderConfigure = orderedList => (a, b) => {
    const startsWithList = orderedList.reduce(
        (result, testItem, ind) => {
            if (a.startsWith(testItem)) {
                result[0] = ind;
            }
            if (b.startsWith(testItem)) {
                result[1] = ind;
            }
            return result;
        },
        [Infinity, Infinity],
    );

    if (startsWithList[0] < startsWithList[1]) {
        return -1;
    }
    if (startsWithList[1] < startsWithList[0]) {
        return 1;
    }
    return 0;
};

// takeValues :: [s] -> {s: a} -> [a]
export const takeValues = keys => subject =>
    keys.reduce((col, item) => (subject[item] ? col.concat([subject[item]]) : col), []);

export const constructRenditionGroups = (renditionsJson, originalUrl) => {
    const ratioOrder = ['16x9', '36x25', '1x1', '9x16', '2x3', '3x4', '21x9', '3x1'];
    const nameOrder = ['small', 'medium', 'large', 'jumbo', 'full'];
    const extraRatios = [];
    const renditionGroups = Object.keys(renditionsJson).reduce((grp, renditionName) => {
        const match = /\d{1,2}x\d{1,2}$/.exec(renditionName);
        if (match) {
            const ratio = match[0];
            if (!ratioOrder.includes(ratio) && !extraRatios.includes(ratio)) {
                extraRatios.push(ratio);
            }
            if (!grp[ratio]) {
                grp[ratio] = {
                    renditionListWithSpaces: '',
                    renditionListWithCommas: '',
                    renditions: [],
                    dimensions: ratio,
                    ratio: ratio.replace('x', ' : '),
                    originalUrl,
                };
            }
            grp[ratio].renditions.push(renditionName);

            const sortedRenditions = grp[ratio].renditions.sort(sortOrderConfigure(nameOrder));
            grp[ratio].renditionListWithSpaces = sortedRenditions.join(' ');
            grp[ratio].renditionListWithCommas = sortedRenditions.join(',');
        }
        return grp;
    }, {});
    return takeValues(ratioOrder.concat(extraRatios))(renditionGroups);
};

const ImageEditorView = Backbone.View.extend({
    template: imageEditorTemplate,
    events: {
        transitionend: '_initEditors',
        'click .sda-image-link': '_toggleLink',
        'click .sda-thumb-container': '_thumbnailClickHandler',
        'click #cancel-image-editor': '_cancel',
        'click .save-image-modal': '_saveBtnHandler',
        'click .sda-image-preview': '_previewImage',
        'exit #image-preview': '_closePreviewFlyover',
        'change #image-editor-title': '_gaLogChangeTitle',
        'change #media-image-caption': '_gaLogChangeCaption',
        'change #image-editor-alt-text': '_gaLogChangeAltText',
    },
    initialize(opts = {}) {
        this.inStory = opts.inStory || false;
        this._registerHandlebarsHelpers();
        this.subViews = [];
        this.imagePreviewView = new ImagePreviewView({el: '#image-preview'});
        this.subViews.push(this.imagePreviewView);
    },
    render(model) {
        const that = this;
        // set model
        that.model = model;
        const parser = new DOMParser();
        const title = that.model?.attributes?.title ? parser.parseFromString(parse(that.model.attributes.title), 'text/html') : '';
        const cleanTitle = (title?.body?.textContent || '').replace(/\u00A0/g, ' ');
        const alt = that.model?.attributes?.alt ? parser.parseFromString(parse(that.model.attributes.alt), 'text/html') : '';
        const cleanAlt = (alt?.body?.textContent || '').replace(/\u00A0/g, ' ');
        that.model.attributes.caption = parse(that.model?.attributes?.caption || '');
        that.model.attributes.title = cleanTitle;
        that.model.attributes.alt = cleanAlt;
        that.image = new Image();
        that.linked = true;

        const modelJson = model.toJSON();
        const renditionsJson = model.get('renditions').toJSON();

        // Clone the current model and freeze it. Used in the instance of canceling model changes.
        that._STARTING_MODEL_ATTRIBUTES = cloneObject(modelJson);
        //choose  default affected renditions
        that.renditionsToCrop = {};
        that.renditionsToCrop.all = Object.keys(renditionsJson)
            .sort()
            .filter(function (rendition) {
                return !rendition.toLowerCase().match('blur') && !rendition.toLowerCase().match('scale');
            });
        that.renditionsToCrop.selected = that.renditionsToCrop.all;
        that.renditionsToCrop.first = ['large16x9', 'medium16x9', 'small16x9'];
        // Ensure that.el is empty and render the default content
        const templateParameters = Object.assign({}, modelJson, {
            inStory: this.inStory,
            renditionGroups: constructRenditionGroups(renditionsJson, modelJson.originalUrl),
            maxCaptionLength: DEFAULT_MEDIA_CAPTION_MAXLENGTH,
        });
        that.$el
            .empty()
            .html(imageEditorTemplate(templateParameters))
            .sdaInitWidgets();
        that.imagePreviewView.el = '#image-preview';
        that.imagePreviewView.$el = $(that.imagePreviewView.el);

        that.image.onload = that._initializeJcrop.bind(that);
        that.image.src = that.model.get('originalUrl');
        /**
         * Media Access Rights
         * Allows users to change access rights of an original media only for the properties user has permission for
         */
        const mediaAccessRightsEle = document.getElementById('MediaAccessRightsSection-Image');
        if (mediaAccessRightsEle && getFeatureFlag('media.accessRights')) {
            const isEditable = this.model.get('original') === 'true' && mediaAccessRightsEle.hasAttribute('data-is-editable');

            const onSave = brandUuidList => {
                this.model.set({brandUuidList});
                this._saveBtnHandler();
            };

            reactRender(
                <MediaAccessRightsSidebarItem
                    selectedBrandUuids={this.model.get('brandUuidList') || []}
                    availableBrandUuids={getAvailableBrandUuids()}
                    availableAdminBrandUuids={getAvailableAdminBrandUuids()}
                    isEditable={isEditable}
                    onSave={onSave}
                />,
                mediaAccessRightsEle,
            );
        }
    },
    _initEditors() {
        const $editors = this.$('.sda-editor');
        if ($editors.length && !$editors.data('sdaEditor')) {
            $editors.sdaEditor().data('sdaEditor');
        }
    },
    validate() {
        let isValid = true;
        //isValid is on the right side of the logical conjuction operator
        //to run all validators even after one of them indicates error.
        const imageCaptionText = $('#media-image-caption').text()
            ?.trim();
        isValid = imageCaptionText?.length > 0 && maxLength($('#media-image-caption')) && isValid;
        if (!isValid) {
            $('#media-image-caption').sdaError();
        }
        this.$el.find('.media-panel-field').each((i, el) => {
            isValid = maxLength(el) && isValid;
        });
        return isValid;
    },
    _initializeJcrop() {
        const that = this;
        const $canvas = $('.sda-media-canvas');
        const $sizeReference = $('.sda-media');
        let cropInfo = that.model.getCropInfoForRendition('large16x9');
        const maxWidth = $sizeReference.width();
        const maxHeight = $sizeReference.height();

        that.$canvasImage = $canvas.find('img');
        that.$canvasImage.css({maxWidth, maxHeight}).attr('src', that.image.src);

        cropInfo = that._baselineCropInfo(cropInfo, 16 / 9);

        const initialViewPair = () => {
            let value = true;
            return [() => value, () => (value = false)];
        };

        //set Jcrop

        const [isInitialView, initiallyViewed] = initialViewPair();
        $canvas.Jcrop(
            {
                onSelect: (...args) => this._updateRendition(isInitialView(), ...args),
                bgColor: that.model.hasLowResolutionRendition() ? 'orange' : 'black',
                bgOpacity: 0.4,
                trueSize: [that.image.width, that.image.height],
                setSelect: [cropInfo.x, cropInfo.y, cropInfo.x2, cropInfo.y2],
                aspectRatio: cropInfo.aspectRatio,
            },
            function () {
                that.jcropApi = this;
                that._updateRenditionThumbnails({useInitialValues: true});
                initiallyViewed();
            },
        );
    },
    // THIS METHOD IS A TEMPORARY BACKFILL IN CASE AN IMAGE DOES NOT COME BACK WITH CROPINFO
    // AFTER A FAILPROOF METHOD FROM THE BACKEND GETS PUT IN PLACE, IT CAN BE REMOVED
    _baselineCropInfo(cropInfo, aspectRatio) {
        const that = this;
        const keysWithMissingVals = Object.keys(cropInfo).filter(function (key) {
            return cropInfo[key] === undefined || cropInfo[key] === null;
        });

        if (keysWithMissingVals.length) {
            cropInfo.aspectRatio = aspectRatio;
            cropInfo.x = 0;
            cropInfo.x2 = that.image.width;
            cropInfo.h = Math.round(cropInfo.x2 / cropInfo.aspectRatio);

            if (cropInfo.h > that.image.height) {
                cropInfo.y = 0;
                cropInfo.y2 = that.image.height;
                cropInfo.w = Math.round(cropInfo.y2 * cropInfo.aspectRatio);
                cropInfo.x = Math.round((that.image.width - cropInfo.w) / 2);
                cropInfo.x2 = cropInfo.x + cropInfo.w;
            } else {
                cropInfo.y = Math.round((that.image.height - cropInfo.h) / 2);
                cropInfo.y2 = +cropInfo.y + cropInfo.h;
            }
        }

        return cropInfo;
    },
    _toggleLink() {
        const that = this;
        const $targets = $('.sda-image-link');

        $targets.toggleClass('fa-link').toggleClass('fa-chain-broken');

        if ($targets.hasClass('fa-link')) {
            $('.sda-thumb-container').removeClass('selected');
            that.renditionsToCrop.selected = that.renditionsToCrop.all;
            that.linked = true;
        } else {
            $('.sda-thumb-container:first').addClass('selected');
            that.renditionsToCrop.selected = that.renditionsToCrop.first;
            that.linked = false;
        }
        const cropInfo = that.model.getCropInfoForRendition(that.renditionsToCrop.selected[0]);

        that.jcropApi.animateTo([cropInfo.x, cropInfo.y, cropInfo.x2, cropInfo.y2]);

        that.jcropApi.setOptions({
            aspectRatio: that.model.getCropInfoForRendition(that.renditionsToCrop.selected[0]).aspectRatio,
        });

        that._resolutionWarning();
    },
    _thumbnailClickHandler(e) {
        const that = this;
        const $caller = $(e.currentTarget);

        if (!that.linked) {
            $('.sda-thumb-container').removeClass('selected');
            $caller.addClass('selected');

            that.renditionsToCrop.selected = $caller.attr('data-select').split(',');
            const cropInfo = that.model.getCropInfoForRendition(that.renditionsToCrop.selected[0]) || {};

            that.jcropApi.animateTo([cropInfo.x, cropInfo.y, cropInfo.x2, cropInfo.y2]);

            that.jcropApi.setOptions({
                aspectRatio: cropInfo.aspectRatio,
            });

            that._resolutionWarning();
        }
    },
    // CropInfo is being passed in from jcrop on select
    _updateRendition(initialRender, cropInfo) {
        // force the initial render to allow the renditions to use their original offsets.
        if (initialRender === true) {
            return;
        }

        const renditions = this.model.get('renditions');
        if (this.linked) {
            this.renditionsToCrop.selected.forEach(function (renditionName) {
                let renditionHeight, renditionWidth;

                const rendition = renditions.get(renditionName);
                const aspectRatio = rendition.get('width') / rendition.get('height');
                const cropAspectRatio = cropInfo.w / cropInfo.h;

                if (aspectRatio.toFixed(2) === cropAspectRatio.toFixed(2)) {
                    renditionHeight = cropInfo.h;
                    renditionWidth = cropInfo.w;
                } else if (cropInfo.w > cropInfo.h) {
                    renditionHeight = cropInfo.h;
                    renditionWidth = renditionHeight * aspectRatio;
                } else {
                    // This route shouldn't be taken if we do not allow vertical crops, but is left here for posterity
                    renditionWidth = cropInfo.w;
                    renditionHeight = renditionWidth * (1 / aspectRatio);
                }

                rendition.set({
                    x: Math.floor(cropInfo.x + (cropInfo.w - renditionWidth) / 2),
                    y: Math.floor(cropInfo.y + (cropInfo.h - renditionHeight) / 2),
                    height: Math.floor(renditionHeight),
                    width: Math.floor(renditionWidth),
                });
            });
        } else {
            this.renditionsToCrop.selected.forEach(function (renditionName) {
                const rendition = renditions.get(renditionName);
                if (rendition) {
                    rendition.set({
                        x: Math.floor(cropInfo.x),
                        y: Math.floor(cropInfo.y),
                        width: Math.floor(cropInfo.w),
                        height: Math.floor(cropInfo.h),
                    });
                }
            });
        }

        this._updateRenditionThumbnails();
        this._resolutionWarning();
        if (this.isJcropInitiated) {
            cropImage(this.model.get('uuid'));
        } else {
            this.isJcropInitiated = true;
        }
    },
    _updateRenditionThumbnails({useInitialValues = false} = {}) {
        this.renditionsToCrop.selected
            .filter(rendition => rendition.startsWith('large'))
            .forEach(rendition => {
                let renditionInfo;
                if (useInitialValues) {
                    renditionInfo = this.model.getRendition(rendition).toJSON();
                } else {
                    renditionInfo = this.model.getCropInfoForRendition(rendition);
                }

                const $renditionThumb = $('#' + rendition);
                const thumbScale = $renditionThumb.width() / renditionInfo.width;
                const thumbWidth = thumbScale * this.image.width;

                $renditionThumb
                    .find('img')
                    .width(thumbWidth)
                    .css({
                        top: -(renditionInfo.y * thumbScale),
                        left: -(renditionInfo.x * thumbScale),
                    });
            });
    },
    _updateModel() {
        const that = this,
            attributes = {
                title: $('#image-editor-title').val(),
                caption: stringify($('#media-image-caption').html()),
                alt: $('#image-editor-alt-text').val(),
            };
        return Promise.resolve(that.model.set(attributes));
    },
    _cancel() {
        const that = this;

        that.model.parse(that._STARTING_MODEL_ATTRIBUTES);
        cancelImageChanges(that.model.get('uuid'));

        if (that.jcropApi) {
            that.jcropApi.destroy();
        }

        eventChannels.media.imageEditor.trigger('cancel', that._STARTING_MODEL_ATTRIBUTES);
        that.$el.empty();
        that._destroySubViews();
    },
    _saveModel() {
        const that = this;

        return new Promise(function (resolve, reject) {
            return that.model
                .save()
                .then(function (response) {
                    console.log('saving finished: ', response);
                    eventChannels.media.imageEditor.trigger('save', response);
                    resolve(this);
                    that.jcropApi.destroy();
                    that._destroySubViews();
                    messageHandler('success', 'Image successfully updated');
                })
                .fail(err => {
                    reject(err);
                    messageHandler('error', 'Image failed to update');
                });
        });
    },
    countErrorMessage() {
        messageHandler('error', countErrorMessage());
    },
    _saveBtnHandler() {
        const $saveBtns = $('.save-image-modal');

        if (this.validate()) {
            this._disableBtn($saveBtns);
            this._updateModel()
                .then(() => this._saveModel())
                .then(() => {
                    this._enableBtn($saveBtns);
                    saveImageChanges(this.model.get('uuid'));
                })
                .catch(err => {
                    this._enableBtn($saveBtns);
                    logError(err);
                });
        } else {
            this.countErrorMessage();
        }
    },
    _disableBtn($el) {
        $el.attr('disabled', '');
    },
    _enableBtn($el) {
        $el.removeAttr('disabled');
    },
    _destroySubViews() {
        const that = this;

        that.subViews.forEach(function (subView) {
            destroy(subView);
        });

        that.subViews = [];
    },
    _previewImage(e) {
        const that = this,
            $caller = $(e.target),
            previewRendtions = $caller.data('preview-renditions').split(' ');
        that.imagePreviewView.render(that.model, previewRendtions);
        $('#image-preview').open();
        previewImage(that.model.get('uuid'));
    },
    _closePreviewFlyover() {
        $('#image-preview').close();
    },
    _resolutionWarning() {
        const that = this;
        const renditionWarnings = that.model.getResolutionWarnings();

        if (renditionWarnings.length) {
            if (
                that.renditionsToCrop.selected.filter(function (renditionName) {
                    return renditionWarnings.includes(renditionName);
                }).length
            ) {
                if (that.jcropApi) {
                    that.jcropApi.setOptions({
                        bgColor: 'orange',
                    });
                }

                eventChannels.media.imageEditor.trigger('addResolutionWarning', that.model);
            }
        } else {
            if (that.jcropApi) {
                that.jcropApi.setOptions({
                    bgColor: 'black',
                });
            }

            eventChannels.media.imageEditor.trigger('removeResolutionWarning', that.model);
        }
    },
    _registerHandlebarsHelpers() {
        const that = this;

        Handlebars.registerHelper('getCropInfo', function (rendition) {
            return JSON.stringify(that.model.getCropInfoForRendition(rendition));
        });
    },
    _attributeChanged(attributeName) {
        const that = this;

        changeImageAttribute(attributeName, {
            label: that.model.get('uuid'),
        });
    },
    _gaLogChangeTitle() {
        this._attributeChanged('title');
    },
    _gaLogChangeCaption() {
        this._attributeChanged('caption');
    },
    _gaLogChangeAltText() {
        this._attributeChanged('alt');
    },
});

export default ImageEditorView;
